import React, { useState, useRef, useEffect } from 'react';
import { Search, Filter, DollarSign, CreditCard, ChevronDown } from 'lucide-react';

export default function ProductFilters({
  searchTerm,
  setSearchTerm,
  selectedLine,
  setSelectedLine,
  productLines,
  setShowPaymentModal,
  paymentMethod,
  setPaymentMethod,
  selectedColor,
  setSelectedColor,
  colors,
  creditOptions,
  accentColor
}) {
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const [showLineDropdown, setShowLineDropdown] = useState(false);
  const paymentDropdownRef = useRef(null);
  const lineDropdownRef = useRef(null);

  const getCardDetails = () => {
    if (paymentMethod.type === 'tarjeta' && paymentMethod.cardId && creditOptions) {
      return creditOptions.find(opt => opt.id === paymentMethod.cardId);
    }
    return null;
  };

  const cardDetails = getCardDetails();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (paymentDropdownRef.current && !paymentDropdownRef.current.contains(event.target)) {
        setShowPaymentDropdown(false);
      }
      if (lineDropdownRef.current && !lineDropdownRef.current.contains(event.target)) {
        setShowLineDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePaymentSelect = (type) => {
    if (type === 'tarjeta') {
      setPaymentMethod({
        ...paymentMethod,
        type: 'tarjeta'
      });
      setShowPaymentDropdown(false);
      setShowPaymentModal(true);
    } else {
      setPaymentMethod({
        type: type,
        cardId: null,
        bank: null
      });
      setShowPaymentDropdown(false);
    }
  };

  const getPaymentDisplayText = () => {
    if (paymentMethod.type === 'efectivo') {
      return 'Efectivo';
    } else if (paymentMethod.type === 'tarjeta' && cardDetails) {
      return `${cardDetails.bank} - ${cardDetails.type}`;
    }
    return 'Tarjetas';
  };

  const getPaymentIcon = () => {
    if (paymentMethod.type === 'efectivo') {
      return <DollarSign className="w-5 h-5" />;
    }
    return <CreditCard className="w-5 h-5" />;
  };

  return (
    <div className="bg-white rounded-3xl p-6 shadow-lg mb-8 mt-4 border border-purple-100 max-w-7xl mx-auto">
      {/* Top Row: Search, Line Filter, Payment */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6 items-end">
        {/* Search - Takes more space */}
        <div className="md:col-span-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-purple-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-purple-50 border-2 border-purple-200 rounded-2xl text-gray-800 placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
            />
          </div>
        </div>

        {/* Line Filter Custom Selector */}
        <div className="md:col-span-3">
          <label className="block text-gray-600 font-semibold mb-2 text-xs uppercase tracking-wider text-center">
            Filtrar por Línea
          </label>
          <div className="relative" ref={lineDropdownRef}>
            <button
              onClick={() => setShowLineDropdown(!showLineDropdown)}
              className={`w-full h-[48px] flex items-center justify-between gap-2 px-4 bg-white border-2 border-purple-200 text-gray-700 rounded-2xl font-semibold transition-all duration-200 hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-400`}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Filter className="w-5 h-5 text-purple-400" />
                <span className="truncate text-sm">
                  {selectedLine === 'all' ? 'Todas las líneas' : selectedLine.charAt(0).toUpperCase() + selectedLine.slice(1)}
                </span>
              </div>
              <ChevronDown className={`w-4 h-4 text-purple-400 transition-transform duration-200 flex-shrink-0 ${showLineDropdown ? 'rotate-180' : ''}`} />
            </button>

            {/* Line Dropdown Menu */}
            {showLineDropdown && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border-2 border-purple-100 overflow-hidden z-50 animate-fadeIn">
                {productLines.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setSelectedLine(cat);
                      setShowLineDropdown(false);
                    }}
                    className={`w-full flex items-center px-4 py-3 text-left transition-all hover:bg-purple-50 border-t first:border-t-0 border-gray-100 ${selectedLine === cat ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-700'
                      }`}
                  >
                    <span className="text-sm">
                      {cat === 'all' ? 'Todas las líneas' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Payment Method Custom Selector */}
        <div className="md:col-span-3">
          <label className="block text-gray-600 font-semibold mb-2 text-xs uppercase tracking-wider text-center">
            Elige tu medio de pago
          </label>
          <div className="relative" ref={paymentDropdownRef}>
            <button
              onClick={() => setShowPaymentDropdown(!showPaymentDropdown)}
              className={`w-full h-[48px] flex items-center justify-between gap-2 px-4 text-white rounded-2xl font-semibold transition-all duration-200 shadow-lg ${!accentColor ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' : 'hover:opacity-90'}`}
              style={accentColor ? { backgroundColor: accentColor } : {}}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {getPaymentIcon()}
                <span className="truncate text-sm">{getPaymentDisplayText()}</span>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${showPaymentDropdown ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {showPaymentDropdown && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border-2 border-purple-100 overflow-hidden z-50 animate-fadeIn">
                <button
                  onClick={() => handlePaymentSelect('efectivo')}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all hover:bg-purple-50 ${paymentMethod.type === 'efectivo' ? 'bg-purple-50 font-semibold' : ''
                    }`}
                >
                  <DollarSign className={`w-5 h-5 ${paymentMethod.type === 'efectivo' ? (accentColor ? '' : 'text-purple-600') : 'text-gray-400'}`} style={paymentMethod.type === 'efectivo' && accentColor ? { color: accentColor } : {}} />
                  <span className={`text-sm ${paymentMethod.type === 'efectivo' ? (accentColor ? '' : 'text-purple-700') : 'text-gray-700'}`} style={paymentMethod.type === 'efectivo' && accentColor ? { color: accentColor } : {}}>Efectivo</span>
                </button>

                <button
                  onClick={() => handlePaymentSelect('tarjeta')}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all hover:bg-purple-50 border-t border-gray-100 ${paymentMethod.type === 'tarjeta' ? 'bg-purple-50 font-semibold' : ''
                    }`}
                >
                  <CreditCard className={`w-5 h-5 ${paymentMethod.type === 'tarjeta' ? (accentColor ? '' : 'text-purple-600') : 'text-gray-400'}`} style={paymentMethod.type === 'tarjeta' && accentColor ? { color: accentColor } : {}} />
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm ${paymentMethod.type === 'tarjeta' ? (accentColor ? '' : 'text-purple-700') : 'text-gray-700'}`} style={paymentMethod.type === 'tarjeta' && accentColor ? { color: accentColor } : {}}>
                      {cardDetails ? `${cardDetails.bank} - ${cardDetails.type}` : 'Tarjetas'}
                    </span>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row: Color Filters */}
      <div>
        <label className="block text-gray-600 font-semibold mb-3 text-xs uppercase tracking-wider">
          Filtrar por Color
        </label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(colors).map(([key, color]) => (
            <button
              key={key}
              onClick={() => setSelectedColor(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-200 ${selectedColor === key
                ? `text-white shadow-lg scale-105 ${!accentColor ? 'bg-gradient-to-r from-purple-600 to-pink-600' : ''}`
                : 'bg-white text-gray-700 hover:bg-purple-50 border-2 border-purple-200'
                }`}
              style={selectedColor === key && accentColor ? { backgroundColor: accentColor } : {}}
            >
              {color.hex && (
                <span
                  className="w-4 h-4 rounded-full border-2 border-white shadow-md"
                  style={{ backgroundColor: color.hex }}
                ></span>
              )}
              <span className="text-sm">{color.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
